import { FileText, Shield, HelpCircle, Zap } from "lucide-react";
import { STRIPE_LINKS } from "../config/siteConfig";

export const appealProblems = [
  { id: "problem-1", title: "Generic Appeals", description: "Copy-paste appeals get ignored. Platforms receive millions of generic requests." },
  { id: "problem-2", title: "Missing Evidence", description: "Without proper documentation, your appeal lacks the proof needed for review." },
  { id: "problem-3", title: "Wrong Channels", description: "Submitting through incorrect forms or departments delays or kills your case." },
  { id: "problem-4", title: "Policy Misunderstanding", description: "Not addressing the specific violation cited makes appeals ineffective." },
];

export const services = [
  {
    id: "service-free",
    title: "Free Assessment",
    price: "FREE",
    priceNote: "No obligation",
    description: "Quick evaluation of your case to determine if we can help.",
    features: ["Case eligibility check", "Platform policy overview", "Honest assessment of chances", "No payment required"],
    cta: "Check Eligibility",
    ctaLink: "/contact",
    highlight: false,
    icon: HelpCircle,
  },
  {
    id: "service-review",
    title: "Case Review",
    price: "$99",
    priceNote: "One-time",
    description: "Detailed analysis of your suspension with actionable guidance.",
    features: ["Full case analysis", "Policy violation breakdown", "Evidence checklist", "Written action plan"],
    cta: "Start Case Review",
    ctaLink: STRIPE_LINKS.caseReview,
    highlight: false,
    icon: FileText,
  },
  {
    id: "service-package",
    title: "Appeal Package",
    price: "$399",
    priceNote: "Best Value",
    description: "Complete documentation preparation and submission support.",
    features: ["Everything in Case Review", "Professional documentation", "We submit through official public appeal channels with your authorization", "Follow-up support included"],
    cta: "Get Appeal Package",
    ctaLink: STRIPE_LINKS.appealPackage,
    highlight: true,
    icon: Shield,
  },
  {
    id: "service-priority",
    title: "Priority Support",
    price: "$499",
    priceNote: "24hr Turnaround",
    description: "Expedited preparation with dedicated priority handling.",
    features: ["Everything in Appeal Package", "24-hour documentation prep", "Priority queue placement", "Direct phone support"],
    cta: "Get Priority Support",
    ctaLink: STRIPE_LINKS.prioritySupport,
    highlight: false,
    icon: Zap,
  },
];

export const steps = [
  { id: "step-1", number: "01", title: "Submit Your Case", description: "Fill out our intake form with your account details, suspension reason, and any evidence you have." },
  { id: "step-2", number: "02", title: "We Review & Analyze", description: "Our team reviews your case, identifies the policy issues, and develops your appeal strategy." },
  { id: "step-3", number: "03", title: "Documentation Prepared", description: "We prepare professional appeal documentation with proper evidence organization." },
  { id: "step-4", number: "04", title: "Appeal Submitted", description: "Appeals are submitted through official platform channels with proper follow-up." },
];

export const testimonials = [
  { id: "testimonial-1", quote: "After 3 failed attempts on my own, Social Dispute Co helped organize my evidence and prepare a professional appeal submission. The documentation was thorough and addressed the specific policy issues.", author: "Sarah M.", role: "Small Business Owner", platform: "Instagram" },
  { id: "testimonial-2", quote: "They were upfront that there are no guarantees, which actually made me trust them more. Professional and organized process.", author: "Marcus T.", role: "Content Creator", platform: "TikTok" },
  { id: "testimonial-3", quote: "The documentation they prepared was thorough. They clearly explained that platform decisions are outside their control, and they followed up through official channels as promised.", author: "Jessica R.", role: "Brand Account Manager", platform: "Facebook" },
];

export const caseStudies = [
  { id: "case-1", platform: "Instagram", issue: "Disabled for 'violating terms'", timeline: "Documentation submitted within 5 business days", outcome: "Appeal submitted through official channels" },
  { id: "case-2", platform: "TikTok", issue: "Permanent ban for 'community guidelines'", timeline: "Full documentation package prepared", outcome: "Appeal submitted with evidence organized by violation type" },
  { id: "case-3", platform: "YouTube", issue: "Channel terminated for 'spam'", timeline: "Documentation submitted within 7 business days", outcome: "Appeal submitted through YouTube's official appeal form" },
];

export const faqs = [
  { id: "faq-1", question: "Can you guarantee my account will be restored?", answer: "No. We cannot and do not guarantee outcomes. Platform decisions are entirely outside our control. What we do guarantee is professional, thorough documentation preparation that gives your appeal the best possible presentation." },
  { id: "faq-2", question: "Are you affiliated with Instagram, Facebook, TikTok, or other platforms?", answer: "No. Social Dispute Co is completely independent. We are not affiliated with Meta, Instagram, Facebook, TikTok, YouTube, X (Twitter), or any social media platform." },
  { id: "faq-3", question: "Do you need my password or account access?", answer: "Never. We do not request passwords, login credentials, or account access. Our service is documentation preparation only." },
  { id: "faq-4", question: "How long does the process take?", answer: "Documentation preparation takes 3-7 business days (24 hours for Priority). Platform response times vary: typically 10 days for acknowledgment, and 14-60 days for final decisions." },
  { id: "faq-5", question: "What if my appeal is denied?", answer: "Our Appeal Package and Priority Support include follow-up documentation at no additional cost. We'll prepare a revised appeal addressing any new information from the denial." },
];
